import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  animation: ${fadeUp} 300ms ease forwards;
`;

const ModalBox = styled.div`
  background: #fffdf9;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  position: relative;
`;

const Title = styled.h2`
  margin-bottom: 16px;
  color: #1f2937;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  &:hover {
    color: #000;
  }
`;

const SurveysContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 20px;
`;

const SurveyCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #ffedd5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const SurveyHeading = styled.h3`
  margin: 0 0 12px 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 700;
`;

const SurveyInfo = styled.div`
  margin-bottom: 8px;
  color: #4b5563;
  font-size: 14px;
`;

const SurveyLabel = styled.span`
  font-weight: 600;
  color: #6b7280;
  margin-right: 6px;
`;

const SurveyorList = styled.div`
  margin-top: 8px;
`;

const SurveyorItem = styled.div`
  padding: 4px 0;
  color: #374151;
  font-size: 13px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
`;

const SurveysModal = ({
  isOpen,
  onClose,
  routeId,
  selectable = false,
  onSurveySelect,
}) => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && routeId) {
      fetchSurveys();
    } else {
      setSurveys([]);
    }
  }, [isOpen, routeId]);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      // let url = selectable
      //   ? `/api/surveys/${routeId}/unfinished`
      //   : `/api/surveys/${routeId}`;
      let url = `/api/surveys/${routeId}`;
      const res = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.surveys || [];
      setSurveys(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching surveys:", err);
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const handleSurveyClick = (surveyId) => {
    if (typeof onSurveySelect === "function") {
      onSurveySelect(surveyId);
      return;
    }
    if (selectable) {
      navigate(`/entries/survey/${surveyId}?routeId=${routeId}`);
    }
  };

  const isCardSelectable = selectable || typeof onSurveySelect === "function";

  const formatTemperature = (value) => {
    if (value === null || value === undefined) return "-";
    const n = Number(value);
    if (Number.isNaN(n)) return "-";
    return `${n.toFixed(1)}°C`;
  };

  const formatHumidity = (value) => {
    if (value === null || value === undefined) return "-";
    const n = Number(value);
    if (Number.isNaN(n)) return "-";
    return `${n}%`;
  };

  return (
    <Overlay
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <Title>Surveys</Title>
        <CloseBtn onClick={onClose}>✖</CloseBtn>

        {loading ? (
          <LoadingState>Loading surveys...</LoadingState>
        ) : surveys.length === 0 ? (
          <EmptyState>No surveys found for this route.</EmptyState>
        ) : (
          <SurveysContainer>
            {surveys.map((survey) => (
              <SurveyCard
                key={survey._id}
                onClick={() =>
                  isCardSelectable && handleSurveyClick(survey._id)
                }
                style={{
                  cursor: isCardSelectable ? "pointer" : "default",
                }}
              >
                <SurveyHeading>
                  Survey {survey.version}{" "}
                  {survey.category === "Vaccination" && `(${survey.category})`}
                </SurveyHeading>
                <SurveyInfo>
                  <SurveyLabel>Travel Mode:</SurveyLabel>
                  <span style={{ textTransform: "capitalize" }}>
                    {survey.travelMode || "N/A"}
                  </span>
                </SurveyInfo>
                <SurveyInfo>
                  <SurveyLabel>Created:</SurveyLabel>
                  {formatDate(survey.createdAt)}
                </SurveyInfo>
                <SurveyInfo>
                  <SurveyLabel>Temperature:</SurveyLabel>
                  {formatTemperature(survey.temperature)}
                </SurveyInfo>
                <SurveyInfo>
                  <SurveyLabel>Humidity:</SurveyLabel>
                  {formatHumidity(survey.humidity)}
                </SurveyInfo>
                <SurveyInfo>
                  <SurveyLabel>Dogs found:</SurveyLabel>
                  {survey.totalDogObservationCount}
                </SurveyInfo>
                {survey.surveyor && survey.surveyor.length > 0 && (
                  <SurveyorList>
                    <SurveyLabel>Surveyors:</SurveyLabel>
                    {survey.surveyor.map((s, idx) => (
                      <SurveyorItem key={s._id || idx}>
                        • {s.name} {s.email && `(${s.email})`}
                      </SurveyorItem>
                    ))}
                  </SurveyorList>
                )}
              </SurveyCard>
            ))}
          </SurveysContainer>
        )}
      </ModalBox>
    </Overlay>
  );
};

export default SurveysModal;
